import React, { useState } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonButtons,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { RefresherEvent } from '../types/ionic';

interface User {
  id: string;
  username: string;
  createdAt: string;
}

interface CreateUserFormValues {
  username: string;
  password: string;
}

const Users: React.FC = () => {
  const { t } = useLanguage();
  const [presentToast] = useIonToast();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { control, handleSubmit, reset, formState } = useForm<CreateUserFormValues>({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await apiClient.get<User[]>('/users');
      return Array.isArray(data) ? data : [];
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (payload: CreateUserFormValues) => {
      await apiClient.post('/users', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      presentToast({
        message: t('userCreatedSuccessfully') ?? 'User created successfully',
        duration: 2000,
        color: 'success',
      });
      reset();
      setIsModalOpen(false);
    },
    onError: () => {
      presentToast({
        message: t('userCreateFailed') ?? 'Failed to create user',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      presentToast({
        message: t('userDeleted') ?? 'User deleted',
        duration: 2000,
        color: 'success',
      });
    },
    onError: () => {
      presentToast({
        message: t('userDeleteFailed') ?? 'Failed to delete user',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await usersQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const onSubmit = (values: CreateUserFormValues) => {
    createUserMutation.mutate(values);
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('users')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <IonButton expand="block" style={{ marginBottom: 16 }} onClick={() => setIsModalOpen(true)}>
            {t('addUser') ?? 'Add user'}
          </IonButton>

          {usersQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : (usersQuery.data ?? []).length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {(usersQuery.data ?? []).map((user) => (
                <IonItem key={user.id} lines="full">
                  <IonLabel>
                    <h3>{user.username}</h3>
                    <p>{new Date(user.createdAt).toLocaleString()}</p>
                  </IonLabel>
                  <IonButton
                    color="danger"
                    fill="clear"
                    slot="end"
                    onClick={() => deleteUserMutation.mutate(user.id)}
                    disabled={deleteUserMutation.isPending}
                  >
                    {t('delete')}
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonModal isOpen={isModalOpen} onDidDismiss={() => setIsModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('addUser') ?? 'Add user'}</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => {
                    setIsModalOpen(false);
                    reset();
                  }}
                >
                  {t('cancel')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <form onSubmit={handleSubmit(onSubmit)}>
              <IonList>
                <IonItem>
                  <IonLabel position="stacked">{t('username')}</IonLabel>
                  <Controller
                    control={control}
                    name="username"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <IonInput
                        value={field.value}
                        onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                      />
                    )}
                  />
                  {formState.errors.username ? (
                    <IonText color="danger">
                      <p>{t('enterUsername')}</p>
                    </IonText>
                  ) : null}
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">{t('password')}</IonLabel>
                  <Controller
                    control={control}
                    name="password"
                    rules={{ required: true, minLength: 6 }}
                    render={({ field }) => (
                      <IonInput
                        type="password"
                        value={field.value}
                        onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                      />
                    )}
                  />
                  {formState.errors.password ? (
                    <IonText color="danger">
                      <p>{t('enterPassword')}</p>
                    </IonText>
                  ) : null}
                </IonItem>
              </IonList>

              <IonButton
                expand="block"
                type="submit"
                style={{ marginTop: 24 }}
                disabled={createUserMutation.isPending}
              >
                {createUserMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
              </IonButton>
            </form>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Users;
